import React, { useState, useEffect, useRef, useCallback } from 'react';
import Alert from 'theme/Alert';
import Promise from 'bluebird';
import { get, debounce, isEmpty } from 'lodash';
import InPageOverlay from 'components/InPageOverlay/InPageOverlay';
import {
  MegadraftEditor,
  editorStateFromRaw,
  editorStateToJSON,
  MegadraftIcons,
} from 'megadraft';
import { PropTypes } from 'prop-types';
import {
  MegadraftEditorWrapper,
  MegadraftEditorWrapperOverides,
  EditorPageWrapper,
  ErrorWrapper,
} from './MegadraftWrapper.styles';
import EditorHeader from './EditorHeader';
// Plugins
import youtube from './plugins/youtube/plugin';
import uploader from './plugins/uploader/plugin';
import productContentPlugin from './plugins/productContentPlugin/plugin';
import table from './plugins/tableview/plugin';
import { pluginPropsShape, editorStateShape } from '../../propTypesObjects';

const ph = {
  blocks: [
    {
      data: {},
      depth: 0,
      entityRanges: [],
      inlineStyleRanges: [
        {
          length: 16,
          offset: 0,
          style: 'BOLD',
        },
      ],
      key: 'filp3',
      text: 'Sample Blog Post Title',
      type: 'header-two',
    },
    {
      data: {},
      depth: 0,
      entityRanges: [],
      inlineStyleRanges: [],
      key: 'd1ieq',
      text: 'An interesting subtitle can be added here',
      type: 'header-three',
    },
    {
      data: {},
      depth: 0,
      entityRanges: [],
      inlineStyleRanges: [],
      key: '9e3j5',
      text: 'This is a paragraph for the your new content. This is a paragraph for the your new content. This is for content. This is a paragraph for the your new paragraph application. This is a paragraph for the your new content. This is for the your new content. This is a paragraph for the your new content',
      type: 'unstyled',
    },
  ],
  entityMap: {},
};

function MegadraftWrapper({
  saveContent,
  content,
  contentId,
  editorOpen,
  clientId,
  onChangeStatus,
  status,
  loading,
  pluginProps,
  setEditorOpen,
  readOnly: readOnlyProps,
  saving: savingProps,
}) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const delayedSave = useRef(
    debounce(async (q) => {
      setSaving(true);
      const save = await saveContent(q);
      if (save && save?.error) {
        setError(save.error);
      } else {
        setError(null);
      }
      await Promise.delay(1500);
      setSaving(false);
    }, 1500)
  ).current;

  const [currentEditorState, setEditorState] = useState(null);
  const [currentMetadata, setMetadata] = useState(null);
  const [currentTags, setTags] = useState(null);
  const [currentCategory, setCategory] = useState(null);
  const [readOnly, setReadOnly] = useState(null);
  const [loadedContent, setLoadedContent] = useState(false);

  const propData = get(content, 'content');
  const hasPropData = !!propData;

  useEffect(() => {
    if (!contentId && !propData) {
      setEditorState(editorStateFromRaw(ph));
    }
  }, [contentId, propData]);

  useEffect(() => {
    if (hasPropData && !loadedContent && contentId) {
      setEditorState(editorStateFromRaw(propData));
      setMetadata({
        title: get(content, 'preview.title', ''),
        description: get(content, 'preview.description', ''),
        image: get(content, 'preview.image', ''),
        slug: get(content, 'slug', ''),
        writer: {
          label: get(content, 'writer_name', ''),
          value: get(content, 'writer', ''),
        },
        company: {
          label: get(content, 'company_name', ''),
          value: get(content, 'company', ''),
        },
        co_company: {
          label: get(content, 'co_company_name', ''),
          value: get(content, 'co_company', ''),
        },
        co_writer: {
          label: get(content, 'co_writer_name', ''),
          value: get(content, 'co_writer', ''),
        },
      });
      setTags(get(content, 'tags', ''));
      setCategory(get(content, 'category', ''));
      setLoadedContent(true);
    }
  }, [contentId, propData, hasPropData, loadedContent, content]);

  const onChange = (editorState) => {
    setEditorState(editorState);
    delayedSave({
      content: JSON.parse(editorStateToJSON(editorState)),
      id: contentId,
    });
  };

  const saveHandler = async ({
    editorState,
    metadata,
    tags: _tags = [],
    category: _category,
  }) => {
    const result = await saveContent({
      content: JSON.parse(editorStateToJSON(editorState || currentEditorState)),
      metadata,
      category: _category,
      tags: _tags,
    });
    setLoadedContent(false);
    return result;
  };

  const publishData = () => {
    const string = JSON.stringify(
      JSON.parse(editorStateToJSON(currentEditorState))
    );
    localStorage.setItem('content', string);
  };

  const previewData = async () => {
    const es = currentEditorState;
    const ro = !readOnly;
    setReadOnly(ro);
    setEditorState(null);
    await Promise.delay(10);
    setReadOnly(ro);
    setEditorState(es);
  };

  const renderIconU = useCallback(
    () => <span style={{ color: '#ccc' }}>U</span>,
    []
  );
  const renderIconC = useCallback(
    () => <span style={{ color: '#ccc' }}>C</span>,
    []
  );
  const renderIconH2 = useCallback(
    () => <span style={{ color: '#ccc' }}>H2</span>,
    []
  );
  const renderIconH3 = useCallback(
    () => <span style={{ color: '#ccc' }}>H3</span>,
    []
  );
  const renderIconH4 = useCallback(
    () => <span style={{ color: '#ccc' }}>H4</span>,
    []
  );
  const renderIconP = useCallback(
    () => <span style={{ color: '#ccc' }}>P</span>,
    []
  );

  const actions = [
    {
      type: 'inline',
      label: 'B',
      style: 'BOLD',
      icon: MegadraftIcons.BoldIcon,
    },
    {
      type: 'inline',
      label: 'U',
      style: 'UNDERLINE',
      icon: renderIconU,
    },
    {
      type: 'inline',
      label: 'I',
      style: 'ITALIC',
      icon: MegadraftIcons.ItalicIcon,
    },
    {
      type: 'inline',
      label: 'C',
      style: 'center',
      icon: renderIconC,
    },
    {
      type: 'entity',
      label: 'Link',
      style: 'link',
      entity: 'LINK',
      icon: MegadraftIcons.LinkIcon,
    },
    { type: 'separator' },
    {
      type: 'block',
      label: 'UL',
      style: 'unordered-list-item',
      icon: MegadraftIcons.ULIcon,
    },
    {
      type: 'block',
      label: 'OL',
      style: 'ordered-list-item',
      icon: MegadraftIcons.OLIcon,
    },
    {
      type: 'block',
      label: 'H1',
      style: 'header-one',
      icon: MegadraftIcons.H2Icon,
    },
    {
      type: 'block',
      label: 'H2',
      style: 'header-two',
      icon: renderIconH2,
    },
    {
      type: 'block',
      label: 'H3',
      style: 'header-three',
      icon: renderIconH3,
    },
    {
      type: 'block',
      label: 'H4',
      style: 'header-four',
      icon: renderIconH4,
    },
    {
      type: 'block',
      label: 'P',
      style: 'paragraph',
      icon: renderIconP,
    },
    {
      type: 'block',
      label: 'QT',
      style: 'blockquote',
      icon: MegadraftIcons.BlockQuoteIcon,
    },
  ];

  return (
    <EditorPageWrapper>
      <InPageOverlay
        full
        disableScroll
        closeOnEsc
        portal
        open={editorOpen}
        innerStyle={{
          top: '0px',
          left: '0px',
          right: '0px',
          bottom: '0px',
        }}
      >
        <div style={{ minWidth: '100%' }}>
          <EditorHeader
            clientId={clientId}
            saving={saving}
            onChangeStatus={onChangeStatus}
            status={status}
            canRenderMeta={
              !contentId || (!loading && !isEmpty(currentMetadata))
            }
            contentId={contentId}
            contentType={pluginProps.contentType}
            objectId={pluginProps.objectId}
            metadata={currentMetadata}
            tags={currentTags}
            category={currentCategory}
            saveContent={saveHandler}
            close={() => setEditorOpen(false)}
            publish={publishData}
            toggleReadOnly={previewData}
            readOnly={readOnlyProps || readOnly}
            pluginProps={pluginProps}
          />
          {error ? (
            <ErrorWrapper>
              <Alert
                summary="item Error"
                view="BlogEditor"
                small
                title={error}
                type="error"
                action={{
                  text: 'Try Saving Again',
                  onClick: onChange.bind(this, currentEditorState),
                }}
              />
            </ErrorWrapper>
          ) : null}
          <div style={{ marginTop: '100px' }}>
            <DraftWrapper
              readOnly={readOnlyProps || readOnly}
              editorState={currentEditorState}
              onChange={onChange}
              loading={loading}
              saving={savingProps}
              contentId={contentId}
              pluginProps={pluginProps}
              actions={actions}
            />
          </div>
        </div>
      </InPageOverlay>
    </EditorPageWrapper>
  );
}

MegadraftWrapper.propTypes = {
  saveContent: PropTypes.func,
  content: PropTypes.shape({
    content: PropTypes.shape({}),
    preview: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.number,
    }),
    slug: PropTypes.string,
    writer: PropTypes.number,
    writer_name: PropTypes.string,
    // LINT OVERRIDE #8
    // TECH DEBT
    // Array has undetermined elements
    // eslint-disable-next-line react/forbid-prop-types
    tags: PropTypes.array,
    category: PropTypes.number,
  }),
  contentId: PropTypes.number,
  editorOpen: PropTypes.bool,
  clientId: PropTypes.number,
  onChangeStatus: PropTypes.func,
  status: PropTypes.string,
  loading: PropTypes.bool,
  pluginProps: PropTypes.shape({
    contentType: PropTypes.number,
    objectId: PropTypes.number,
  }),
  setEditorOpen: PropTypes.func,
  readOnly: PropTypes.bool,
  saving: PropTypes.bool,
};

export function DraftWrapper({
  pluginProps,
  publicPage,
  editorState,
  readOnly,
  contentId,
  saving,
  loading,
  onChange,
  actions,
}) {
  const [refreshingState, setReRender] = useState(null);
  const [isSaving, setSaving] = useState(null);
  async function setRefreshingState() {
    setReRender(true);
  }

  useEffect(() => {
    if (refreshingState && saving) {
      setSaving(true);
    } else if (refreshingState && !saving) {
      setSaving(false);
      setReRender(false);
    }
  }, [refreshingState, saving]);

  let currentEditorState = editorState;
  if (publicPage) {
    currentEditorState = editorStateFromRaw(editorState);
  }

  return (
    <MegadraftEditorWrapper>
      <MegadraftEditorWrapperOverides>
        <div className="mega-draft-editor-wrapper">
          {currentEditorState && (!contentId || !loading) && !isSaving ? (
            <MegadraftEditor
              // blockStyleFn={myBlockStyleFn}
              readOnly={readOnly}
              editorState={currentEditorState}
              onChange={onChange}
              plugins={[
                uploader(pluginProps, setRefreshingState),
                productContentPlugin(pluginProps, setRefreshingState),
                youtube,
                table,
              ]}
              actions={actions}
            />
          ) : null}
        </div>
      </MegadraftEditorWrapperOverides>
    </MegadraftEditorWrapper>
  );
}

DraftWrapper.propTypes = {
  pluginProps: pluginPropsShape,
  publicPage: PropTypes.bool,
  editorState: editorStateShape,
  readOnly: PropTypes.bool,
  contentId: PropTypes.number,
  saving: PropTypes.bool,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      style: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

export default MegadraftWrapper;
